import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { CleaningServices as CleanIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { passkitAPI } from '../../services/api';
import { logger } from '../../utils/logger';

interface CleanResourceTemplatesDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CleanResourceTemplatesDialog: React.FC<CleanResourceTemplatesDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resourceType, setResourceType] = useState<'productions' | 'campaigns' | 'programs'>('productions');
  const [resources, setResources] = useState<any[]>([]);
  const [selectedResource, setSelectedResource] = useState<string>('');
  const [unusedTemplates, setUnusedTemplates] = useState<string[]>([]);
  const [deletedCount, setDeletedCount] = useState(0);

  useEffect(() => {
    if (open) {
      loadResources();
    } else {
      // Reset state when dialog closes
      setUnusedTemplates([]);
      setDeletedCount(0);
      setError(null);
      setSelectedResource('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resourceType]);

  const loadResources = async () => {
    try {
      let response;
      switch (resourceType) {
        case 'productions':
          response = await passkitAPI.getCompanyProductions();
          setResources(response.productions || []);
          break;
        case 'campaigns':
          response = await passkitAPI.getCompanyCampaigns();
          // Campaigns returns array directly, not wrapped
          setResources(Array.isArray(response) ? response : response.campaigns || []);
          break;
        case 'programs':
          response = await passkitAPI.getCompanyPrograms();
          setResources(response.programs || []);
          break;
      }
    } catch (err: any) {
      logger.error('Error loading resources:', err);
      setError('Greška pri učitavanju resursa');
    }
  };

  const analyzeTemplates = async () => {
    if (!selectedResource) {
      setError('Molimo izaberite resurs');
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      // Get unused template IDs from backend (now works for all resource types!)
      const result = await passkitAPI.findUnusedTemplates(selectedResource, resourceType);

      // Set unused templates
      setUnusedTemplates(result.unused_templates || []);

      logger.log('📊 Template Analysis:', {
        resourceId: selectedResource,
        resourceType,
        unusedTemplates: result.unused_templates,
        usedTemplates: result.used_template_ids,
        totalTemplates: result.total_templates
      });

      // Show appropriate message
      if (result.unused_templates && result.unused_templates.length > 0) {
        setError(null); // Clear error, will show warning in UI
      } else {
        setError('Svi template-i su u upotrebi - nema nekorišćenih template-a za brisanje.');
      }
    } catch (err: any) {
      logger.error('Error analyzing templates:', err);
      setError(err.response?.data?.detail || 'Greška pri analizi template-a');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleClean = async () => {
    if (unusedTemplates.length === 0) {
      onClose();
      return;
    }

    setLoading(true);
    setError(null);
    setDeletedCount(0);

    try {
      // Delete each unused template
      for (const templateId of unusedTemplates) {
        try {
          await passkitAPI.deleteTemplate(selectedResource, templateId, resourceType);
          setDeletedCount(prev => prev + 1);
          logger.log(`✅ Deleted template: ${templateId}`);
        } catch (err: any) {
          logger.error(`❌ Failed to delete template ${templateId}:`, err);
          // Continue with other templates even if one fails
        }
      }

      // Show success and reload
      onSuccess();
      handleClose();
    } catch (err: any) {
      logger.error('Error cleaning templates:', err);
      setError(err.response?.data?.detail || 'Greška pri brisanju template-a');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !analyzing) {
      setUnusedTemplates([]);
      setDeletedCount(0);
      setError(null);
      setSelectedResource('');
      onClose();
    }
  };

  const getResourceTypeLabel = () => {
    switch (resourceType) {
      case 'productions': return 'Produkcije';
      case 'campaigns': return 'Kampanje';
      case 'programs': return 'Članstva';
      default: return 'Resursi';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CleanIcon color="primary" />
          Očisti Nekorišćene Template-e
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Resource Type Selector */}
          <FormControl fullWidth>
            <InputLabel>Tip Resursa</InputLabel>
            <Select
              value={resourceType}
              label="Tip Resursa"
              onChange={(e) => setResourceType(e.target.value as any)}
              disabled={analyzing || loading}
            >
              <MenuItem value="productions">Produkcije (Event Tickets)</MenuItem>
              <MenuItem value="campaigns">Kampanje (Coupons)</MenuItem>
              <MenuItem value="programs">Članstva (Membership)</MenuItem>
            </Select>
          </FormControl>

          {/* Resource Selector */}
          <FormControl fullWidth>
            <InputLabel>{getResourceTypeLabel()}</InputLabel>
            <Select
              value={selectedResource}
              label={getResourceTypeLabel()}
              onChange={(e) => setSelectedResource(e.target.value)}
              disabled={analyzing || loading || resources.length === 0}
            >
              {resources.map((resource) => (
                <MenuItem key={resource.id} value={resource.id}>
                  {resource.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Analyze Button */}
          <Button
            variant="outlined"
            onClick={analyzeTemplates}
            disabled={!selectedResource || analyzing || loading}
            startIcon={analyzing ? <CircularProgress size={20} /> : <CleanIcon />}
          >
            {analyzing ? 'Analiziram...' : 'Analiziraj Template-e'}
          </Button>

          {error && (
            <Alert severity={error.includes('nema') ? 'info' : 'error'}>
              {error}
            </Alert>
          )}

          {analyzing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
              <CircularProgress />
              <Typography>Analiziram template-e...</Typography>
            </Box>
          ) : unusedTemplates.length === 0 && !error ? (
            <Alert severity="info">
              Izaberite resurs i kliknite "Analiziraj Template-e" da pronađete nekorišćene template-e.
            </Alert>
          ) : unusedTemplates.length > 0 ? (
            <>
              <Alert severity="warning">
                <Typography variant="body2" gutterBottom>
                  <strong>Pronađeno {unusedTemplates.length} nekorišćenih template-a</strong>
                </Typography>
                <Typography variant="caption">
                  Ovi template-i nisu povezani ni sa jednim ticket type-om i mogu se bezbedno obrisati.
                </Typography>
              </Alert>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Template ID-evi za brisanje:
                </Typography>
                <List dense>
                  {unusedTemplates.map((templateId) => (
                    <ListItem key={templateId}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DeleteIcon fontSize="small" color="error" />
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {templateId}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {loading && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Obrisano: {deletedCount} / {unusedTemplates.length}
                  </Typography>
                  <CircularProgress size={24} sx={{ mt: 1 }} />
                </Box>
              )}
            </>
          ) : null}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading || analyzing}
        >
          {unusedTemplates.length === 0 ? 'Zatvori' : 'Otkaži'}
        </Button>
        {unusedTemplates.length > 0 && (
          <Button
            onClick={handleClean}
            variant="contained"
            color="error"
            disabled={loading || analyzing}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loading ? `Brisanje... (${deletedCount}/${unusedTemplates.length})` : `Obriši ${unusedTemplates.length} template-a`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CleanResourceTemplatesDialog;
